import {
  DocumentCallback,
  OnDocumentLoadSuccess,
  PageCallback,
} from 'node_modules/react-pdf/dist/esm/shared/types'
import { PDFPageProxy } from 'pdfjs-dist/types/src/display/api'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import styled from 'styled-components'
import { Text } from '~/components/core'
import { Loading } from '~/components/Loading'
import { PdfToolbar } from '~/components/Pdfs/PdfToolbar'
import { light } from '~/constants/theme'
import { useFilePrinter } from '~/hooks/useFilePrinter'
import { useWindowDimensions } from '~/hooks/useWindowDimensions'
import { openInNewTab } from '~/utils/functions'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`

// Polyfill for pdf.js notes here https://github.com/wojtekmaj/react-pdf/issues/1811#issuecomment-2157866061
// @ts-expect-error This does not exist outside of polyfill which this is doing
if (typeof Promise.withResolvers === 'undefined') {
  if (window)
    // @ts-expect-error This does not exist outside of polyfill which this is doing
    window.Promise.withResolvers = function () {
      let resolve, reject
      const promise = new Promise((res, rej) => {
        resolve = res
        reject = rej
      })
      return { promise, resolve, reject }
    }
}

const makePageCallback = (page: PDFPageProxy, scale: number): PageCallback => {
  Object.defineProperty(page, 'width', {
    get() {
      return this.view[2] * scale
    },
    configurable: true,
  })
  Object.defineProperty(page, 'height', {
    get() {
      return this.view[3] * scale
    },
    configurable: true,
  })
  Object.defineProperty(page, 'originalWidth', {
    get() {
      return this.view[2]
    },
    configurable: true,
  })
  Object.defineProperty(page, 'originalHeight', {
    get() {
      return this.view[3]
    },
    configurable: true,
  })
  return page as PageCallback
}

export const PdfViewerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  justify-content: center;
  align-items: center;
  padding: 12px;
`
export const PdfContainer = styled.div`
  display: flex;
  flex: 1;
  box-sizing: border-box;
  min-width: 99.99%;
  max-width: 99.99%;
  flex-basis: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fafafc;
  border-radius: 8px;
  position: relative;
  overflow: hidden !important;
  padding-top: 80px;
  padding-bottom: 36px;
  border: solid 2px #e4e4eb;
`

export const PdfContents = styled.div`
  box-shadow: 0 0 10px #1d1d301a;
`

export const ToolbarWrapper = styled.div`
  display: flex;
  width: 100%;
  position: absolute;
  z-index: 2;
  top: 0;
  height: 58px;
  overflow: hidden;
  border-bottom: solid 2px #f0f0f0;
`

interface PdfViewerProps {
  file: string
  filename: string
  pageNumber?: number
  onFaxPdfLoaded?: (numPages: number) => void
  onPageChange?: (pageNumber: number) => void
}

const docOptions = {
  isOffscreenCanvasSupported: true,
}

type PageData = PageCallback & {
  originalImageData?: {
    data: ImageData
    originHeight: number
    originWidth: number
  }
}

export const PdfViewer = ({
  file,
  filename,
  pageNumber: externalPageNumber,
  onFaxPdfLoaded,
  onPageChange,
}: PdfViewerProps) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [pages, setPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [rotation, setRotation] = useState<number>(0)
  const [pageScale, setPageScale] = useState<number>(1)

  const cachedPageRefs = useRef<PageData[]>([])
  const documentRef = useRef<DocumentCallback>()
  const pageCanvasRef = useRef<HTMLCanvasElement | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const [printFile] = useFilePrinter({
    fileURL: file,
    onPrintShortcut: () => {
      window.gtag('event', 'fax_preview_print_key', {})
    },
    onPrintError: () => {
      // Rather than just not work, we are going to open in a new tab so they can print from there.
      // Most people will not see this fallback behavior
      window.gtag('event', 'fax_preview_print_fallback', {})
      openInNewTab(file)
    },
  })

  const getCachedPage = (pageNum: number) => {
    if (!cachedPageRefs.current[pageNum - 1]) {
      return undefined
    }

    return cachedPageRefs.current[pageNum - 1]
  }

  const getPageRef = async (pageNum: number) => {
    // Pages are 1 based indexes, and don't load in order, so we have to check if the specific index is already loaded
    if (!cachedPageRefs.current[pageNum - 1]) {
      const page = await documentRef.current?.getPage(pageNum)
      if (!page) {
        return undefined
      }

      const pageCallback = makePageCallback(page, 1)
      cachedPageRefs.current.push(pageCallback)
      return pageCallback
    }

    return cachedPageRefs.current[pageNum - 1]
  }

  const getPageDimensions = (
    page: PageData,
    pageRotation = 0,
  ): {
    height: number
    width: number
    aspectRatio: number
  } => {
    const pageHeight = page.originalHeight
    const pageWidth = page.originalWidth

    const rotationAwareHeight =
      pageRotation === 90 || pageRotation === 270 ? pageWidth : pageHeight
    const rotationAwareWidth =
      pageRotation === 90 || pageRotation === 270 ? pageHeight : pageWidth

    const aspectRatio = rotationAwareWidth / rotationAwareHeight

    return {
      height: rotationAwareHeight,
      width: rotationAwareWidth,
      aspectRatio,
    }
  }

  const tempCanvas = useRef<HTMLCanvasElement>()
  const resizeCanvas = (
    canvas: HTMLCanvasElement,
    page: PageData,
    scale: number,
  ) => {
    const ctx = canvas.getContext('2d')!
    if (!page.originalImageData) {
      page.originalImageData = {
        data: ctx.getImageData(0, 0, canvas.width, canvas.height),
        originHeight: canvas.height,
        originWidth: canvas.width,
      }

      tempCanvas.current = document.createElement('canvas')
      tempCanvas.current.height = canvas.height
      tempCanvas.current.width = canvas.width

      const tempCtx = tempCanvas.current.getContext('2d')!
      tempCtx.putImageData(page.originalImageData.data, 0, 0)
    }

    const sourceImageData = page.originalImageData
    const { originWidth } = sourceImageData

    const pageDimensions = getPageDimensions(page, rotation)
    const computedHeight = pageDimensions.height * scale
    const computedWidth = pageDimensions.width * scale

    canvas.height = computedHeight
    canvas.width = computedWidth

    //have to update the style as well as the width/height attributes due to how react-pdf renders the canvas
    canvas.style.height = `${computedHeight}px`
    canvas.style.width = `${computedWidth}px`

    const computedScaleFactor = computedWidth / originWidth

    ctx.drawImage(tempCanvas.current!, 0, 0, computedWidth, computedHeight)
    ctx.scale(computedScaleFactor, computedScaleFactor)
  }

  const computePageContainerScale = (page: PageData, pageRotation = 0) => {
    const rect = containerRef.current!.getBoundingClientRect()
    const headerHeight = 60
    const topPadding = 20
    const bottomPadding = 36
    const containerHeight =
      rect.height - headerHeight - topPadding - bottomPadding
    const containerWidth = rect.width

    let scale = 1
    const {
      height: rotationAwareHeight,
      width: rotationAwareWidth,
      aspectRatio: pageAspectRatio,
    } = getPageDimensions(page, pageRotation)

    //is the page taller than it is wide?
    if (pageAspectRatio < 1) {
      //if the page is taller than it is wide, we need to scale the height to fit
      scale = containerHeight / rotationAwareHeight

      //if the scaled width is too wide, we need to scale the width to fit
      if (scale * rotationAwareWidth > containerWidth) {
        scale = containerWidth / rotationAwareWidth
      }
    } else {
      //if the page is wider than it is tall, we need to scale the width to fit
      scale = containerWidth / rotationAwareWidth

      //if the scaled height is too tall, we need to scale the height to fit
      if (scale * rotationAwareHeight > containerHeight) {
        scale = containerHeight / rotationAwareHeight
      }
    }

    return scale
  }

  const getExternalPageNumber = (pageCount: number) => {
    if (externalPageNumber === undefined) {
      return undefined
    }

    return Math.min(Math.max(externalPageNumber, 1), pageCount)
  }

  const handleSetPageNumber = async (newPageNumber: number) => {
    const page = await getPageRef(newPageNumber)
    if (!page) {
      return
    }

    setRotation(0)
    setPageNumber(newPageNumber)
    setLoading(false)
    const newPageScale = computePageContainerScale(page)
    setPageScale(newPageScale)
    onPageChange?.(newPageNumber)
  }

  const handlePageNumberChanged = (delta: number) => {
    const newPageNumber = pageNumber + delta
    if (newPageNumber < 1 || newPageNumber > pages) {
      return
    }

    handleSetPageNumber(newPageNumber)
  }

  const onDocumentLoadSuccess: OnDocumentLoadSuccess = (document) => {
    documentRef.current = document
    cachedPageRefs.current = []
    setPages(document.numPages)
    onFaxPdfLoaded?.(document.numPages)

    const pageNum = getExternalPageNumber(document.numPages)
    handleSetPageNumber(pageNum ?? 1)
  }

  const handleOpenInTab = () => {
    window.gtag('event', 'fax_preview_opened_in_tab', {})
    openInNewTab(file)
  }

  const handleDownload = async () => {
    window.gtag('event', 'fax_preview_downloaded', {})

    const img = await fetch(file)
    const blob = await img.blob()
    const imgUrl = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = imgUrl
    anchor.download = filename.replace('.tif', '.pdf')

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    URL.revokeObjectURL(imgUrl)
  }

  const handleRotate = () => {
    window.gtag('event', 'fax_preview_rotated', {})
    const newRotation = (rotation + 90) % 360
    setRotation(newRotation)

    const newPageScale = computePageContainerScale(
      getCachedPage(pageNumber)!,
      newRotation,
    )
    setPageScale(newPageScale)
  }

  //this is done to prevent the canvas from flickering when the scale changes - the page is not re-rendered when pageScale changes - only rotation, page number, or file reference.
  const handleContainerResized = () => {
    if (loading || !containerRef.current) {
      return
    }

    const cachedPage = getCachedPage(pageNumber)
    if (!cachedPage || !pageCanvasRef.current) {
      return
    }

    const newPageScale = computePageContainerScale(cachedPage, rotation)
    resizeCanvas(pageCanvasRef.current, cachedPage, newPageScale)
  }

  const dimensions = useWindowDimensions()
  useEffect(() => {
    handleContainerResized()
  }, [dimensions])

  const handlePageLoaded = (page: PageData) => {
    cachedPageRefs.current[pageNumber - 1] = page
  }

  const handlePrintFile = () => {
    window.gtag('event', 'fax_preview_print', {})
    printFile()
  }

  // UseEffect for navigating left and right on keydown events
  useEffect(() => {
    const handleArrowKeyPressed = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          window.gtag('event', 'fax_preview_prev_pg_key', {})
          handlePageNumberChanged(-1)
          break
        case 'ArrowRight':
          event.preventDefault()
          window.gtag('event', 'fax_preview_next_pg_key', {})
          handlePageNumberChanged(1)
          break
        default:
          return
      }
    }

    document.addEventListener('keydown', handleArrowKeyPressed)

    return () => {
      document.removeEventListener('keydown', handleArrowKeyPressed)
    }
  }, [handlePageNumberChanged])

  //cache the page until rotation or page number changes
  const cachedPage = useMemo(() => {
    if (loading) {
      return <div />
    }

    const page = getCachedPage(pageNumber)
    if (page) {
      page.originalImageData = undefined
      tempCanvas.current = undefined
    }

    return (
      <Page
        onLoadSuccess={handlePageLoaded}
        canvasRef={pageCanvasRef}
        pageNumber={pageNumber}
        scale={pageScale}
      />
    )
  }, [loading, pageNumber, pageScale])

  useEffect(() => {
    const resolvedExternalPage = getExternalPageNumber(pages)
    if (
      resolvedExternalPage === undefined ||
      resolvedExternalPage === pageNumber
    ) {
      return
    }

    handleSetPageNumber(resolvedExternalPage)
  }, [externalPageNumber])

  const resolvedPageNumber = getExternalPageNumber(pages) ?? pageNumber
  return (
    <PdfViewerWrapper>
      <PdfContainer ref={containerRef}>
        <ToolbarWrapper>
          {!loading && (
            <PdfToolbar
              pages={pages}
              pageNumber={resolvedPageNumber}
              onPageNumberChanged={handlePageNumberChanged}
              onOpenInTab={handleOpenInTab}
              onDownload={handleDownload}
              onRotate={handleRotate}
              onPrint={handlePrintFile}
            />
          )}
        </ToolbarWrapper>
        <PdfContents>
          <Document
            file={file}
            onLoadStart={() => setLoading(true)}
            onLoadSuccess={onDocumentLoadSuccess}
            error={
              <Text text="Unable to load this file" color={light.red200} />
            }
            loading={<Loading />}
            rotate={rotation}
            options={docOptions}
          >
            {cachedPage}
          </Document>
        </PdfContents>
      </PdfContainer>
    </PdfViewerWrapper>
  )
}
